import { useState } from 'react';
import { getRequestQuotesByCommunityId } from '@/api/requests';
import { toTitleCase } from '@/utils/to-title-case';
import { AttachFile, Flaky } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Notepad as NotepadIcon, Plus } from '@phosphor-icons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit02 } from '@untitled-ui/icons-react';
import { useParams } from 'react-router-dom';

import type { File as QuoteFile } from '@/types/file';
import { Meeting_Type } from '@/types/v2/meeting';
import type { Quote} from '@/types/v2/quote';
import { Quote_Status } from '@/types/v2/quote';
import { paths } from '@/paths';
import ReviewQuoteDialog from '@/pages/request/dialogs/ReviewQuoteDialog';
import ViewQuoteDialog from '@/pages/request/dialogs/ViewQuoteDialog';
import CreateUpdateQuoteDialog from '@/pages/v2/requests/request/components/create-quote-dialog';
import { dayjs } from '@/lib/v2/dayjs';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { RouterLink } from '@/components/core/v2/link';
import DeleteActionDialog from '@/components/delete-action-dialog';

interface RequestQuotesCardProps {
  quotes: Quote[];
}

export function RequestQuotesCard(): React.JSX.Element {
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [selectedFile, setSelectedFile] = useState<QuoteFile | null>(null);
  const { community } = useUser();
  const { requestId } = useParams();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<RequestQuotesCardProps>({
    queryKey: ['request-quotes', requestId],
    queryFn: async () => {
      const response = await getRequestQuotesByCommunityId(community?.id, requestId, {
        params: {
          include: 'status,meeting',
        },
      });
      return response;
    },
  });
  const dialog = useDialog();
  const quoteDialog = useDialog();
  const reviewQuoteDialog = useDialog();
  const deleteQuoteDialog = useDialog();
  const viewQuoteDialog = useDialog();
  console.log(data);

  const handleDeleteQuote = (quote: Quote) => {
    deleteQuoteDialog.handleOpen();
  };

  const handleReviewQuote = (quote: Quote) => {
    setSelectedQuote(quote);
    reviewQuoteDialog.handleOpen();
  };

  return (
    <>
      <Card>
        <CardHeader
          action={
            <Button
              color="primary"
              onClick={() => {
                quoteDialog.handleOpen();
                setSelectedQuote(null);
              }}
              startIcon={<Plus />}
              variant="text"
            >
              Create Quote
            </Button>
          }
          avatar={
            <Avatar>
              <NotepadIcon fontSize="var(--Icon-fontSize)" />
            </Avatar>
          }
          title="Quotes"
        />

        <Box display="flex" flexDirection="column" gap={2} maxHeight="300px" overflow="auto" p={2}>
          {data?.quotes?.map((quote: Quote) => (
            <Card key={quote.id} sx={{ flex: '0 0 auto', overflow: 'hidden' }}>
              <CardHeader
                action={
                  <Tooltip arrow title={quote.status.description}>
                    <Chip
                      label={toTitleCase(quote.status.name)}
                      size="small"
                      sx={{
                        bgcolor: theme.palette.background.paper,
                        color: quote.status.color,
                        fontWeight: 'bold',
                        p: 0.5,
                      }}
                    />
                  </Tooltip>
                }
                subheader={dayjs(quote.created_at).format('ll')}
                sx={{ bgcolor: `${quote.status.color}95` }}
                title={`$${parseFloat(quote.total)?.toFixed(2)}`}
              />
              <CardContent sx={{ overflowY: 'auto' }}>
                <Stack spacing={3}>
                  {quote.files?.[0] ? <Button
                      color="inherit"
                      onClick={() => {
                        setSelectedQuote(quote);
                        setSelectedFile(
                          quote.files?.[0]
                            ? {
                                ...quote.files[0],
                                mime_type: quote.files[0].type || quote.files[0].type || '',
                              }
                            : null
                        );
                        viewQuoteDialog.handleOpen();
                      }}
                      startIcon={<AttachFile />}
                      sx={{ maxWidth: '300px', width: '100%', m: 'auto', mt: -2 }}
                      variant="text"
                    >
                      View quote
                    </Button> : null}
                  <Box>
                    <Typography fontWeight="500" mb={1} variant="body1">
                      Description:
                    </Typography>
                    <Typography variant="body2">{quote.description}</Typography>
                  </Box>
                  {quote.payment_terms ? <Box>
                      <Typography fontWeight="500" mb={1} variant="body1">
                        Payment terms:
                      </Typography>
                      <Typography variant="body2">{quote.payment_terms}</Typography>
                    </Box> : null}
                  {quote.payment_type ? <Box>
                      <Typography fontWeight="500" mb={1} variant="body1">
                        Payment type:
                      </Typography>
                      <Typography variant="body2">{toTitleCase(quote.payment_type)}</Typography>
                    </Box> : null}
                  {quote.meeting_id ? <Box>
                      <Typography fontWeight="500" mb={1} variant="body1">
                        Meeting:
                      </Typography>
                      <Typography
                        component={RouterLink}
                        href={`${paths.meetings.index}/${quote.meeting_id}`}
                        sx={{
                          border: 'solid 1px transparent',
                          borderRadius: '5px',
                          p: 1,
                          backgroundColor: 'primary.alpha8',
                        }}
                        variant="body2"
                      >
                        {`${
                          quote.meeting?.meeting_type_id && toTitleCase(Meeting_Type[quote.meeting?.meeting_type_id])
                        } - ${dayjs(quote.meeting?.held_on ?? quote.meeting?.updated_at).format('DD/MM/YYYY')}`}
                      </Typography>
                    </Box> : null}

                  <Stack direction="row" gap={2} justifyContent="flex-end">
                    {quote.status_id === Quote_Status.PENDING && (
                      <Stack>
                        <Tooltip arrow title="Edit quote">
                          <IconButton
                            aria-label="Edit quote"
                            onClick={() => {
                              setSelectedQuote(quote);
                              quoteDialog.handleOpen();
                            }}
                          >
                            <Edit02 color="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                    <Stack>
                      <Tooltip arrow title="Review quote">
                        <IconButton
                          aria-label="Review quote"
                          onClick={() => {
                            setSelectedQuote(quote);
                            reviewQuoteDialog.handleOpen();
                          }}
                        >
                          <Flaky sx={{ color: 'inherit' }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Stack>
                      <DeleteActionDialog
                        buttonSize="medium"
                        buttonVariant="text"
                        iconOnly
                        onConfirm={() => {
                          handleDeleteQuote(quote);
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
        {data?.quotes?.length === 0 && (
          <Alert severity="info" variant="filled">
            No Quotes
          </Alert>
        )}
      </Card>
      <CreateUpdateQuoteDialog
        communityId={community?.id!}
        onClose={quoteDialog.handleClose}
        open={quoteDialog.open}
        quote={selectedQuote}
        requestId={requestId!}
      />
      <ReviewQuoteDialog
        onClose={reviewQuoteDialog.handleClose}
        onRefresh={() => {
          queryClient.invalidateQueries({ queryKey: ['request-quotes', requestId] });
        }}
        open={reviewQuoteDialog.open}
        quote={selectedQuote}
      />
      <ViewQuoteDialog file={selectedFile} onClose={viewQuoteDialog.handleClose} open={viewQuoteDialog.open} />
    </>
  );
}
